/**
 * Générateur — Automatisme : identifier les patrons de pyramides données.
 * Attendu ID : 1240 | Niveau : 3ème | Domaine : Espace et géométrie,
 * Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 l. 1075 « Identifier les
 * patrons de pyramides données (par exemple inscrites dans un cube). »).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * EXTRACTION de gen_185 patron_qcm_unique et estpatron_qcm_unique (objectif
 * 4e) avec des distracteurs RETOUCHÉS : en 185, le pavé et le cylindre
 * s'éliminaient sans regarder ; ici les faux patrons sont des patrons de
 * pyramides (mauvais nombre de triangles, face manquante, faces qui se
 * recouvrent) et, une fois sur trois, un prisme. Bases régulières à 3, 4 ou
 * 6 côtés (patronPyramide de 4e) ; la base pentagonale est HORS plage
 * (fiche « Autant de triangles que de côtés », n = 5). La pyramide inscrite
 * dans un cube, exemple du programme, est absente de 185 : DIFFÉRÉE (À
 * ARBITRER).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Quel patron ? » (patron_qcm_unique) : la pyramide en
 *     perspective (sommets nommés, pyramide.js), quatre patrons en images.
 *   Carte 2 « Est-ce un patron ? » (estpatron_qcm_unique) : un schéma, « Oui »
 *     / « Non : il manque une face » / « Non : deux faces se recouvrent ».
 *   Carte 3 « Combien de triangles ? » (flash_texte_trou_saisie) : « Le
 *     patron d'une pyramide à base hexagonale a ⬚ triangles. »
 *   Carte 4 « Vrai ou faux » (sens_tableau_vf) : quatre lignes sur les
 *     patrons (nombre de triangles, de faces, la base).
 *
 * INVARIANTS : les patrons sont construits par patronPyramide (n, variante),
 * le solveur RELIT chaque image (polygones : la base à n côtés, les
 * triangles, un triangle rabattu vers l'intérieur = recouvrement) ; un
 * patron de pyramide à n côtés a exactement n triangles ; distracteurs
 * distincts. Pas de sacrifice (la fiche est hors plage, n = 5).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   nombre_de_triangles  : le patron d'une autre base (6 triangles pour 4), PRINCIPALE ;
 *   face_manquante       : un triangle de moins ;
 *   recouvrement         : une face rabattue sur une autre ;
 *   prisme_pour_pyramide : le patron d'un pavé ;
 *   patron_cru           : « oui » devant un schéma qui ne se plie pas ;
 *   base_oubliee         : n faces au lieu de n + 1.
 */
import {
    randomChoice, randInt, shuffle, pickLettresSains,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { patronPyramide } from '../../4eme/espace/svgEspace';
import { patronPave } from '../../5eme/espace/svgSolides';
import { svgPyramideNommee, NOM_BASE } from './pyramide';

const ATTENDU_ID = 1240;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PATRON = 'Quel patron ?';
const L_EST = 'Est-ce un patron ?';
const L_FLASH = 'Combien de triangles ?';
const L_VF = 'Vrai ou faux';

const BASES = [{ n: 3, base: 'triangle' }, { n: 4, base: 'carre' }, { n: 6, base: 'hexagone' }];
const POOL = 'ABCDEFGHJKLMNPRSTUV'.split('');
const salt = () => randInt(0, 999999);
const imgChoix = (svg) => ({ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` } });

const EXPLICATIONS = {
    regle: 'Le patron d\'une pyramide, c\'est la base et autant de triangles que la base a de côtés, chacun accroché à un côté.',
    nombre_de_triangles: 'Je compte les côtés de la base : une base carrée, c\'est 4 triangles, ni plus ni moins.',
    face_manquante: 'Chaque côté de la base porte un triangle : s\'il en manque un, la pyramide serait ouverte.',
    recouvrement: 'Un triangle rabattu par-dessus un autre ne peut pas se plier : les faces se chevaucheraient.',
    prisme_pour_pyramide: 'Un patron fait de rectangles se plie en pavé ou en prisme, pas en pyramide : il faut des triangles.',
    base_oubliee: 'Les faces, c\'est les triangles ET la base : 4 + 1 = 5 pour une base carrée.',
};

/** Une pyramide nommée pour l'énoncé (sommets par pickLettresSains). */
const pyramideNommee = (b) => {
    const [sommet, ...lettres] = pickLettresSains(POOL, b.n + 1);
    return { base: b.base, sommet, lettres, nom: `${sommet}${lettres.join('')}` };
};

// ─── Carte 1 : quel patron ? (QCM d'images) ───────────────────────────────

const genPatron = () => {
    const b = randomChoice(BASES);
    const py = pyramideNommee(b);
    const bon = imgChoix(patronPyramide(b.n, 'correct', { salt: salt() }));
    const autreN = randomChoice(BASES.filter((x) => x.n !== b.n)).n;
    const cands = shuffle([
        { code: 'face_manquante', svg: patronPyramide(b.n, 'manque', { salt: salt() }) },
        { code: 'recouvrement', svg: patronPyramide(b.n, 'recouvre', { salt: salt() }) },
        { code: 'prisme_pour_pyramide', svg: patronPave({ salt: salt() }).svg },
    ]);
    const faux = [{ code: 'nombre_de_triangles', svg: patronPyramide(autreN, 'correct', { salt: salt() }) }, ...cands.slice(0, 2)];
    const payload = makeQcm([bon, ...faux.map((f) => imgChoix(f.svg))], bon, `<p>${py.nom} est une pyramide à base ${NOM_BASE[b.base]}.</p><p><b>Quel est son patron ?</b></p>`, { ...opts(L_PATRON), elements_additionnels: imgElement(svgPyramideNommee(py), 'large') });
    payload.contenu = { ...payload.contenu, sousType: b.base, explications: EXPLICATIONS };
    payload._meta.params = { n: b.n, autreN, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2 : est-ce un patron ? (QCM) ───────────────────────────────────

const REPONSES = { correct: 'Oui, c\'est un patron.', manque: 'Non : il manque une face.', recouvre: 'Non : deux faces se recouvrent.' };
const CODE_REPONSE = { correct: 'patron_cru', manque: 'face_manquante', recouvre: 'recouvrement' };

const genEstPatron = () => {
    const b = randomChoice(BASES);
    const variante = randomChoice(['correct', 'manque', 'recouvre']);
    const payload = makeQcm(Object.values(REPONSES), REPONSES[variante], `<p>Voici un schéma présenté comme le patron d'une pyramide à base ${NOM_BASE[b.base]}.</p><p><b>Est-ce un patron ?</b></p>`, { ...opts(L_EST), elements_additionnels: imgElement(patronPyramide(b.n, variante, { salt: salt() }), 'large') });
    payload.contenu = { ...payload.contenu, sousType: variante, explications: EXPLICATIONS };
    payload._meta.params = { n: b.n, variante, distracteurs: Object.keys(REPONSES).filter((v) => v !== variante).map((v) => ({ code: CODE_REPONSE[v] })) };
    return payload;
};

// ─── Carte 3 : combien de triangles ? (flash) ─────────────────────────────

const genFlash = () => {
    const bases = shuffle(BASES);
    const lignes = bases.map((b, i) => (i === 0
        ? `Le patron d'une pyramide à base ${NOM_BASE[b.base]} a {{${b.n}}} triangles.`
        : randInt(0, 1) ? `Le patron d'une pyramide à base ${NOM_BASE[b.base]} a {{${b.n}}} triangles.` : `Une pyramide à base ${NOM_BASE[b.base]} a {{${b.n + 1}}} faces en tout.`));
    const payload = makeTexteTrou(lignes.join('\n'), '<p><b>Complète.</b></p>', { ...opts(L_FLASH), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, base_oubliee: EXPLICATIONS.base_oubliee } };
    payload._meta.params = { bases: bases.map((b) => b.n) };
    return payload;
};

// ─── Carte 4 : Vrai ou faux ───────────────────────────────────────────────

const genVf = () => {
    const b = randomChoice(BASES); const n = b.n; const nb = NOM_BASE[b.base];
    const autre = randomChoice(BASES.filter((x) => x.n !== n)).n;
    const candidats = [
        { l: `Le patron d'une pyramide à base ${nb} a ${n} triangles.`, v: true, fam: 'triangles' },
        { l: `Le patron d'une pyramide à base ${nb} a ${autre} triangles.`, v: false, fam: 'triangles', code: 'nombre_de_triangles' },
        { l: `Une pyramide à base ${nb} a ${n + 1} faces.`, v: true, fam: 'faces' },
        { l: `Une pyramide à base ${nb} a ${n} faces.`, v: false, fam: 'faces', code: 'base_oubliee' },
        { l: `Dans le patron d'une pyramide à base ${nb}, chaque triangle touche un côté de la base.`, v: true, fam: 'base' },
        { l: `Dans le patron d'une pyramide à base ${nb}, la base est un triangle.`, v: n === 3, fam: 'base', code: n === 3 ? null : 'nombre_de_triangles' },
        { l: 'Un patron fait seulement de rectangles peut se plier en pyramide.', v: false, fam: 'forme', code: 'prisme_pour_pyramide' },
        { l: 'Dans un patron de pyramide, deux faces ne se recouvrent jamais.', v: true, fam: 'forme' },
    ];
    for (let essai = 0; essai < 100; essai++) {
        const nbFaux = randInt(1, 3);
        const fausses = shuffle(candidats.filter((x) => !x.v)); const vraies = shuffle(candidats.filter((x) => x.v));
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((x) => x.fam)).size !== 4) continue;
        const payload = makeVraiFaux(lignes.map((x) => x.l), lignes.map((x) => (x.v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', { ...opts(L_VF), elements_additionnels: imgElement(patronPyramide(n, 'correct', { salt: salt() }), 'large') });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { n, autre, distracteurs: lignes.filter((x) => x.code).map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_1240 : V/F sans tirage');
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'bases régulières à 3, 4 ou 6 côtés (n = 5 hors plage, fiche) ; patrons construits par patronPyramide, relus par le solveur',
        'la pyramide inscrite dans un cube (exemple du programme) est différée',
    ],
    patron_qcm_unique: 'nombre_de_triangles toujours présent (le patron d\'une autre base, la principale), puis deux parmi face_manquante, recouvrement, prisme_pour_pyramide (un pavé)',
    estpatron_qcm_unique: 'trois réponses fixes : oui, il manque une face, deux faces se recouvrent',
    sens_tableau_vf: 'quatre familles exclusives : triangles, faces, base, forme du patron',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    patron_qcm_unique: genPatron,
    estpatron_qcm_unique: genEstPatron,
    flash_texte_trou_saisie: genFlash,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'patron', label: L_PATRON, formatsList: [{ key: 'patron_qcm_unique', label: 'QCM' }] },
    { id: 'estpatron', label: L_EST, formatsList: [{ key: 'estpatron_qcm_unique', label: 'QCM' }] },
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
